import React, {useState} from 'react';
import {Card} from 'react-bootstrap';
import {BaseModal} from 'components/modals';
import Button from '@/components/ui/Button';
import useData from '@/utils/useData';
import CellLoader from '@/components/loading/CellLoader';
import TableComponent from '@/components/table/Table';
import i18n from 'i18n';
import {useFormattedDateTime} from '@/components/utils/FormattedDateTime';
import {buildURL} from '@/utils/urlBuilder';

interface ProcessingLogItem {
    id: number;
    logItemTypeDisplay: string;
    timeElapsed?: string;
    statusDisplay: string;
    additionalInfo?: {
        evaluationTaskName?: string;
        [key: string]: any;
    };
}

interface ProcessingLog {
    id: number;
    createdOn: string;
    finishedOn: string | null;
    logItems: ProcessingLogItem[];
}

interface ProcessingLogsResponse {
    results: ProcessingLog[];
}

interface ProcessingLogsTableProps {
    eventContentId: number;
    eventType: string;
}

const ProcessingLogsTable: React.FC<ProcessingLogsTableProps> = React.memo((
    {
        eventContentId,
        eventType,
    },
) => {
    const {formatDateTime, isLoaded: configLoaded} = useFormattedDateTime();
    const [detailsProcessingModalOpen, setDetailsProcessingModalOpen] = useState<boolean>(false);
    const [detailsProcessing, setDetailsProcessing] = useState<Record<string, any>>({});

    const [processingLogs, loading] = useData<ProcessingLogsResponse>(buildURL('eventProcessingLogs'), {
        useLocalStore: true,
        requestParams: {
            params: {
                [`event__event_${eventType}`]: eventContentId,
            },
        },
    });

    if (loading) {
        return (
            <CellLoader fullHeight={true} />
        );
    }

    if (processingLogs.results.length === 0) {
        return (
            <span className="m-3">
                {i18n.t('jt_data_available.text')}
            </span>
        )
    }

    const columns = [
        {
            header: i18n.t('th_step.text'),
            accessorKey: 'logItemTypeDisplay',
            cell: (cell: { row: { original: ProcessingLogItem } }) => {
                const step = cell.row.original.logItemTypeDisplay;
                const evaluation = cell.row.original.additionalInfo?.evaluationTaskName;

                if (step === 'Event evaluations' && evaluation) {
                    return (
                        <>
                            {step} ({evaluation})
                        </>
                    );
                }
                return step;
            },
        },
        {
            header: i18n.t('th_elapsed_sec.text'),
            accessorKey: 'timeElapsed',
            cell: (cell: { row: { original: ProcessingLogItem } }) => {
                const timeElapsed = cell.row.original.timeElapsed;
                if (!timeElapsed) {
                    return i18n.t('n_a.text');
                }
                const timeParts = timeElapsed?.split(':') ?? [];
                const secondsAndMilliseconds = timeParts[2] ?? '';

                return secondsAndMilliseconds.slice(0, 7);
            },
        },
        {
            header: i18n.t('th_status.text'),
            accessorKey: 'statusDisplay',
        },
        {
            header: i18n.t('th_details.text'),
            cell: (cell: { row: { original: ProcessingLogItem } }) => {
                if (cell.row.original.additionalInfo && Object.keys(cell.row.original.additionalInfo).length) {
                    return (
                        <Button
                            variant="info"
                            size="sm"
                            onClick={() => {
                                setDetailsProcessing(cell.row.original.additionalInfo || {});
                                setDetailsProcessingModalOpen(true);
                            }}
                        >
                            {i18n.t('jt_show.text')}
                        </Button>
                    );
                }

                return null;
            },
        },
    ];

    const processingLogsView = processingLogs.results.map((log: ProcessingLog) => (
        <React.Fragment key={`logcard-${log.id}`}>
            <Card>
                <Card.Body>
                    <div className="d-flex justify-content-between align-items-center">
                        <div className="d-flex flex-row w-100 justify-content-between">
                            <p className="mr-4 processing-log-id">{i18n.t('processing_log_id.text')} {log.id}</p>

                            <div className="d-flex justify-content-between flex-grow-1 text-right">
                                <p className="mr-3 processing-log-text-nowrap">
                                    {`Started: ${configLoaded ? formatDateTime(log.createdOn, false, {setZone: false}) : ''}`}
                                </p>

                                {log.finishedOn !== null ? (
                                    <p className="mr-3 processing-log-text-nowrap">
                                        {`Finished on: ${configLoaded ? formatDateTime(log.finishedOn, false, {setZone: false}) : ''}`}
                                    </p>
                                ) : (
                                    <p className="mr-3 processing-log-text-nowrap">{i18n.t('jt_unfinished.text')}</p>
                                )}
                            </div>
                        </div>
                    </div>

                    <TableComponent data={log.logItems} columns={columns} />
                </Card.Body>
            </Card>
        </React.Fragment>
    ));

    return (
        <>
            {processingLogsView}
            <BaseModal
                isOpen={detailsProcessingModalOpen}
                onClose={() => setDetailsProcessingModalOpen(false)}
                title={i18n.t('jt_processing_log.text')}
                size="xl"
            >
                <pre>
                    {JSON.stringify(detailsProcessing, null, 4)}
                </pre>
            </BaseModal>
        </>
    );
});


export default ProcessingLogsTable; 